import { memo, ReactNode } from 'react';
import { Image, Typography } from '~/shared/components';
import { ProductPrice } from '~/shared/ProductPrice/ProductPrice';
import { useTranslation } from '~/shared/utils/translation';
import { useSpecialPrice } from '~/services/special-price';
import type { Image as ImageType } from '~/lib/data-contract/salesforce';
import styles from './ReservationProductCard.module.scss';
import { SalesforceProduct } from '~/lib/salesforce/types';

type ReservationProductCardProps = {
    product: SalesforceProduct;
    image?: ImageType;
    quantitySelector: ReactNode;
};

export const ReservationProductCard = memo(
    ({ product, image, quantitySelector }: ReservationProductCardProps) => {
        const { translate } = useTranslation();
        const { prices: specialPriceList } = useSpecialPrice({
            id: product.id,
            c_prices: product.c_prices,
            c_specialPrices: product.c_specialPrices,
        });

        const prices = specialPriceList ?? product.c_prices;

        return (
            <article className={styles.reservationProductCard}>
                <div className={styles.productImage}>
                    {image && (
                        <Image
                            layout="fill"
                            sizes="200px"
                            objectFit="cover"
                            src={image.link}
                            className={styles.productImageMedia}
                        />
                    )}
                </div>
                <div className={styles.productContent}>
                    <Typography
                        as="p"
                        variant="paragraph-L"
                        weight="medium"
                        className={styles.productBrand}
                    >
                        {product.brand}
                    </Typography>
                    <Typography as="h3" variant="paragraph-M" className={styles.productName}>
                        {product.name}
                    </Typography>
                    <Typography as="p" variant="paragraph-XS" className={styles.productDescription}>
                        {product.c_synopsis}
                    </Typography>

                    <div className={styles.purchaseInfo}>
                        <div className={styles.priceList}>
                            {prices && (
                                <ProductPrice
                                    minimumPrice={product.c_minimumPrice}
                                    isGiftCard={
                                        product.c_group === 'electronicgiftcard' ||
                                        product.c_group === 'physicalgiftcard'
                                    }
                                    promoType={product.c_activepromo?.type}
                                    defaultLabel={translate('pdp.defaultPriceLabel')}
                                    prices={prices}
                                    moduleType="reservationProduct"
                                />
                            )}
                        </div>
                        <div className={styles.quantitySelector}>{quantitySelector}</div>
                    </div>
                </div>
            </article>
        );
    },
);

ReservationProductCard.displayName = 'ReservationProductCard';
